/**
 * One route table, used for two things: routing, and building the navigation.
 *
 * Each entry carries the permission it needs. The nav is generated by
 * filtering this list against the signed-in role's permission set, so a
 * section with no permitted children simply does not render — nobody
 * maintains a second list of "what this role sees", and a link can never
 * appear that leads to a 403.
 */

import type { ReactNode } from 'react'

export interface Screen {
  path: string
  title: string
  /** Shown in the top bar under the title. */
  blurb?: string
  group: string
  /** The permission required to reach it. Absent means "any signed-in user". */
  permission?: string
  element: ReactNode
  /** Reachable by URL but hidden from the sidebar. */
  hidden?: boolean
  /**
   * Roles this screen is not part of the job of.
   *
   * Not a permission and never a substitute for one: the API still answers
   * these roles, because reading what the company billed is not something an
   * Admin is kept out of. This is about a navigation that describes the work
   * a person actually does -- the Admin runs the system and does not sell.
   */
  notForRoles?: readonly string[]
}

import AdminDashboard from './views/Dashboard'
import Locations from './views/Locations'
import Departments from './views/Departments'
import People from './views/People'
import ChartOfAccounts from './views/ChartOfAccounts'
import Approvals from './views/Approvals'
import ApprovalPolicies from './views/ApprovalPolicies'
import Banking from './views/Banking'
import Periods from './views/Periods'
import PeriodDetail from './views/PeriodDetail'
import Reports from './views/Reports'
import ReportLibrary, { ReportPage } from './views/ReportLibrary'
import AuditLog from './views/AuditLog'
import Vendors from './views/Vendors'
import VendorLedger from './views/VendorLedger'
import Purchasing from './views/Purchasing'
import Bills from './views/Bills'
import Payables from './views/Payables'
import Payments from './views/Payments'
import Rules from './views/Rules'
import Reconciliation from './views/Reconciliation'
import Exceptions from './views/Exceptions'
import ExpensesView from './views/Expenses'
import PaymentRequests from './views/PaymentRequests'
import PettyCash from './views/PettyCash'
import ApiKeys from './views/ApiKeys'
import ClinicBilling from './views/ClinicBilling'
import MoneyIn from './views/MoneyIn'
import ClinicDrawer from './views/ClinicDrawer'
import Company from './views/Company'
import Performance from './views/Performance'
import CashFlow from './views/CashFlow'
import Budgets from './views/Budgets'
import ActivityReport from './views/Activity'
import MoneyFlows from './views/MoneyFlows'
import Pipeline from './views/Pipeline'
import Trace from './views/Trace'
import Calendar from './views/Calendar'
import Billing from './views/Billing'
import Receivables from './views/Receivables'
import CounterTakings from './views/CounterTakings'
import Contracts from './views/Contracts'
import Commitments from './views/Commitments'
import Financing from './views/Financing'
import Capital from './views/Capital'
import GroupCompanies from './views/GroupCompanies'
import Vendor360 from './views/Vendor360'
import Tds from './views/Tds'
import Gst from './views/Gst'
import BankQuestions from './views/BankQuestions'
import DirectorLedgers from './views/DirectorLedgers'
import InvestmentLedgers from './views/InvestmentLedgers'
import Journals from './views/Journals'

//: Billing, takings, debtors and price lists: the selling side, which this
//: practice does not run through the system. Nothing is raised there, so the
//: screens are four empty pages that make the navigation longer and the job
//: less obvious.
//:
//: Off the navigation rather than out of the permissions, deliberately. The
//: data is still readable to whoever could read it before, so switching this
//: back on is deleting one line rather than a permission change, a migration
//: and a new row in the control-model snapshot. If the practice does start
//: invoicing, that is exactly what should happen.
const NOT_IN_USE = ['admin', 'finance_head', 'book_keeper', 'employee'] as const

export const SCREENS: Screen[] = [
  {
    path: '/',
    title: 'Dashboard',
    group: 'Overview',
    element: <AdminDashboard />,
  },

  {
    path: '/books/banking',
    title: 'Bank statements',
    blurb: 'Import, categorise, and post. Categorising is not posting.',
    group: 'Books',
    permission: 'banking.transaction.view',
    element: <Banking />,
  },
  {
    path: '/books/money-in',
    title: 'Money in',
    blurb:
      'Every credit the clinics earned, matched to its receipts and invoices, and posted split into revenue, GST and the bank’s charge.',
    group: 'Books',
    permission: 'banking.transaction.view',
    element: <MoneyIn />,
  },
  {
    path: '/books/approvals',
    title: 'Approvals',
    blurb:
      'Work waiting on a decision — the department first, then finance. You cannot approve what you prepared.',
    group: 'Books',
    // Everybody may have something to decide or something they are waiting
    // on: department heads sign in as Employees.
    permission: 'inbox.item.view_own',
    element: <Approvals />,
  },
  {
    path: '/books/journals',
    title: 'Journals',
    blurb:
      'Manual journals: the Book Keeper submits, a Finance Head approves, the Book Keeper posts.',
    group: 'Books',
    permission: 'gl.journal.view',
    element: <Journals />,
  },
  {
    path: '/books/periods',
    title: 'Periods & close',
    blurb: 'The checklist is derived fresh on every visit, never cached.',
    group: 'Books',
    permission: 'gl.period.view',
    element: <Periods />,
  },
  {
    path: '/books/periods/:label',
    title: 'Close checklist',
    group: 'Books',
    permission: 'gl.period.view',
    element: <PeriodDetail />,
    hidden: true,
  },
  {
    path: '/books/reports',
    title: 'Financial statements',
    blurb: 'Trial balance, profit and loss, and balance sheet — all derived from the ledger.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <Reports />,
  },
  {
    path: '/books/report-library',
    title: 'Reports',
    blurb:
      'Every report — statements, ledgers, day book, activity — on screen and as an Excel download.',
    group: 'Books',
    permission: 'report.operational.view',
    element: <ReportLibrary />,
  },
  {
    path: '/books/report-library/:key',
    title: 'Report',
    blurb: 'One report, with its filters and an Excel download.',
    group: 'Books',
    permission: 'report.operational.view',
    element: <ReportPage />,
    hidden: true,
  },
  {
    path: '/books/performance',
    title: 'Clinic performance',
    blurb: 'What each clinic earned and cost. The same journal lines as the company accounts.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <Performance />,
  },
  {
    path: '/books/activity',
    title: 'Activity',
    blurb:
      'How much went through and what state it is all in — counts as well as amounts, ' +
      'month by month, including the debits that were flagged and have since been ' +
      'accepted. The tiles answer what needs you now; this answers what happened.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <ActivityReport />,
  },
  {
    path: '/books/money-flows',
    title: 'Money in & out',
    blurb:
      'Grouped by what the other side of the entry was, so a loan received never reads as ' +
      'income and one instalment splits itself into repayment and interest.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <MoneyFlows />,
  },
  {
    path: '/books/budgets',
    title: 'Budgets',
    blurb:
      'What each department means to spend, against what is posted and what is already ' +
      'promised. A budget refuses nothing; it is the figure an approver judges against.',
    group: 'Books',
    permission: 'budget.budget.view_own',
    element: <Budgets />,
    //: Off the menu at the business's request (14 Oct 2026); search and
    //: links still open it.
    hidden: true,
  },
  {
    path: '/books/pipeline',
    title: 'Spend & forecast',
    blurb:
      'Asked for, committed, paid and posted — kept apart, because a budget is overspent ' +
      'long before the ledger says so.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <Pipeline />,
    //: Off the menu at the business's request (14 Oct 2026); search and
    //: links still open it.
    hidden: true,
  },
  {
    path: '/books/cash-flow',
    title: 'Cash flow',
    blurb: 'Where the cash went. An explanation that does not add up says which account broke it.',
    group: 'Books',
    permission: 'report.financial.view',
    element: <CashFlow />,
  },

  {
    path: '/selling/billing',
    title: 'Customer invoices',
    blurb: 'Our claim on somebody. Raised by one person and checked by another before it goes out.',
    group: 'Selling',
    permission: 'ar.invoice.view',
    notForRoles: NOT_IN_USE,
    element: <Billing />,
  },
  {
    path: '/selling/counter',
    title: 'Counter takings',
    blurb: 'Where most of the revenue comes from. A day has to add up before it can post.',
    group: 'Selling',
    permission: 'ar.takings.view',
    notForRoles: NOT_IN_USE,
    element: <CounterTakings />,
  },
  {
    path: '/selling/clinic-billing',
    title: 'Clinic billing',
    blurb:
      'Every bill and payment the clinic software records, sent as it changes, added up by day beside the drawer. Not yet in the books.',
    group: 'Selling',
    permission: 'clinic.billing.view',
    element: <ClinicBilling />,
  },
  {
    path: '/selling/receivables',
    title: 'What we are owed',
    blurb: 'Customer-wise, aged, and checked against the ledger on every read.',
    group: 'Selling',
    permission: 'ar.ageing.view',
    notForRoles: NOT_IN_USE,
    element: <Receivables />,
  },
  {
    path: '/selling/contracts',
    title: 'Rate contracts',
    blurb: 'What was agreed, and until when. A price is superseded, never edited.',
    group: 'Selling',
    permission: 'ar.contract.view',
    notForRoles: NOT_IN_USE,
    element: <Contracts />,
  },

  {
    path: '/buying/purchasing',
    title: 'Requests & orders',
    blurb: 'A request is a need and an order is a commitment. Neither touches the ledger.',
    group: 'Buying',
    permission: 'proc.purchase_request.view_own',
    element: <Purchasing />,
  },
  {
    path: '/buying/bills',
    title: 'Vendor bills',
    blurb: 'Ordered, received and invoiced have to agree before a bill is payable.',
    group: 'Buying',
    permission: 'ap.invoice.view',
    element: <Bills />,
  },
  {
    path: '/buying/payables',
    title: 'What we owe',
    blurb: 'Vendor-wise, aged, and checked against the ledger on every read.',
    group: 'Buying',
    permission: 'ap.ageing.view',
    element: <Payables />,
    //: Off the menu at the business's request (14 Oct 2026); search and
    //: links still open it.
    hidden: true,
  },
  {
    path: '/buying/payments',
    title: 'Payment runs',
    blurb: 'Prepared by one person, approved by another, released by a third.',
    group: 'Buying',
    permission: 'ap.payment.view',
    element: <Payments />,
    //: Off the menu at the business's request (14 Oct 2026); search and
    //: links still open it.
    hidden: true,
  },
  {
    path: '/buying/commitments',
    title: 'Commitments',
    blurb:
      'Rent, AMCs, insurance. Approved once for the term; each payment is raised by a person, ' +
      'never by a clock.',
    group: 'Buying',
    permission: 'commit.commitment.view_own',
    element: <Commitments />,
  },
  {
    path: '/buying/subscriptions',
    title: 'Subscriptions',
    blurb: 'Software paid ahead of the service: an asset until the months it covers have run.',
    group: 'Buying',
    permission: 'commit.commitment.view_own',
    element: <Commitments only="subscription" />,
  },
  {
    path: '/buying/vendors',
    title: 'Vendors',
    blurb: 'Bank details are never edited — a change is proposed, and someone else approves it.',
    group: 'Buying',
    permission: 'party.vendor.view',
    element: <Vendors />,
  },
  {
    path: '/buying/vendor-ledger',
    title: 'Vendor ledger',
    blurb:
      'What each vendor was billed, has been paid and is still owed — bill by bill, with every payment against each.',
    group: 'Buying',
    permission: 'party.ledger.view',
    element: <VendorLedger />,
  },
  {
    path: '/buying/vendors/:id',
    title: 'Vendor',
    blurb: 'Everything about one vendor: owed, committed, held, withheld, and paid.',
    group: 'Buying',
    permission: 'party.ledger.view',
    element: <Vendor360 />,
    hidden: true,
  },

  {
    path: '/financing/loans',
    title: 'Loans',
    blurb:
      'Money the company borrowed and money it lent. What each one stands at is read from the ' +
      'ledger, never stored.',
    group: 'Financing',
    permission: 'fin.instrument.view',
    element: <Financing />,
  },
  {
    path: '/financing/loans-taken',
    title: 'Loans taken',
    blurb: 'What the company owes, to whom, and what falls due next.',
    group: 'Financing',
    permission: 'fin.instrument.view',
    element: <Financing only="borrowing" />,
  },
  {
    path: '/financing/loans-given',
    title: 'Loans given',
    blurb:
      'What is owed to the company. Interest stops being income once collection looks doubtful.',
    group: 'Financing',
    permission: 'fin.instrument.view',
    element: <Financing only="lending" />,
  },

  {
    path: '/financing/capital',
    title: 'Capital & ownership',
    blurb:
      'Money put into the company, the shares issued for it, and who owns what — as at any date ' +
      'you ask about.',
    group: 'Financing',
    permission: 'fin.equity.view',
    element: <Capital />,
  },
  {
    path: '/financing/group-companies',
    title: 'Group companies',
    blurb:
      'Our side is posted, their side is mirrored, and a balance is true once both companies ' +
      'agree it.',
    group: 'Financing',
    permission: 'fin.intercompany.view',
    element: <GroupCompanies />,
  },

  {
    path: '/spending/payment-requests',
    title: 'Payment requests',
    blurb:
      'Ask for money, see who has it, follow it to the bank. The department approves, then finance.',
    group: 'Spending',
    permission: 'spend.payment_request.view_own',
    element: <PaymentRequests />,
  },
  {
    path: '/spending/payment-requests/:id/trace',
    title: 'Following a payment',
    blurb:
      'Who asked, who approved and on what, what was paid, which bank line it matched, and ' +
      'the entry it became.',
    group: 'Spending',
    permission: 'spend.payment_request.view_own',
    element: <Trace />,
    hidden: true,
  },
  {
    path: '/spending/claims',
    title: 'Expense claims',
    blurb: 'What you spent on the company. Your own claims, or the whole clinic.',
    group: 'Spending',
    permission: 'exp.claim.view_own',
    element: <ExpensesView />,
  },
  {
    path: '/spending/tills',
    title: 'Cash floats',
    blurb: 'Count the drawer against the books. A difference is recorded, not absorbed.',
    group: 'Spending',
    permission: 'exp.petty_cash.view',
    element: <PettyCash />,
  },
  {
    path: '/spending/clinic-drawer',
    title: 'Clinic drawer',
    blurb:
      'The clinics’ cash drawer as the clinic software reports it, tied out day by day and posted. Cash only; never revenue.',
    group: 'Spending',
    permission: 'clinic.drawer.view',
    element: <ClinicDrawer />,
  },

  {
    path: '/tax/calendar',
    title: 'Compliance calendar',
    blurb: 'What is due and what was done. The dates are worked out, never stored.',
    group: 'Tax',
    permission: 'compliance.calendar.view',
    element: <Calendar />,
  },
  {
    path: '/tax/tds',
    title: 'Tax withheld',
    blurb: 'What came off each supplier bill, why, and what is owed to the government.',
    group: 'Tax',
    permission: 'tax.tds.view',
    element: <Tds />,
  },
  {
    path: '/tax/gst',
    title: 'GST',
    blurb:
      'Input credit is claimed because a person decided to. 2B is evidence, not an instruction.',
    group: 'Tax',
    permission: 'tax.gst_return.view',
    element: <Gst />,
  },

  {
    path: '/books/questions',
    title: 'Payment questions',
    blurb:
      'A bank line nobody can explain, and the person who can. Not an approval — the money has already gone.',
    group: 'Books',
    // Every role: the Employee who is asked holds nothing else on this screen,
    // and the row scope hands them the one line they were asked about.
    permission: 'banking.question.view_own',
    element: <BankQuestions />,
  },
  {
    path: '/books/director-ledgers',
    title: 'Director ledgers',
    blurb: 'What the company owes each director, one ledger each under 2530 Loans from Directors.',
    group: 'Books',
    permission: 'party.ledger.view',
    element: <DirectorLedgers />,
  },
  {
    path: '/books/investment-ledgers',
    title: 'Investment ledgers',
    blurb:
      'Each mutual fund, FD or RD the company holds, one ledger each under 1530 Current investments.',
    group: 'Books',
    permission: 'party.ledger.view',
    element: <InvestmentLedgers />,
  },
  {
    path: '/books/rules',
    title: 'Categorisation rules',
    blurb: 'When a narration looks like this, it is probably that.',
    group: 'Books',
    permission: 'banking.rule.view',
    element: <Rules />,
  },
  {
    path: '/control/reconciliation',
    title: 'Reconciliation',
    blurb: 'When the bank and the books disagree, the unmatched rows are the work.',
    group: 'Control',
    permission: 'recon.match.view',
    element: <Reconciliation />,
  },
  {
    path: '/control/exceptions',
    title: 'Needs looking at',
    blurb: 'Raised by checks that run again. A fixed problem closes itself.',
    group: 'Control',
    permission: 'compliance.exception.view',
    element: <Exceptions />,
  },

  {
    path: '/setup/company',
    title: 'The company',
    blurb: 'Who we are, and which statutory duties we carry. The calendar is built from this.',
    group: 'Setup',
    permission: 'org.profile.view',
    element: <Company />,
  },
  {
    path: '/setup/chart-of-accounts',
    title: 'Chart of accounts',
    blurb: 'What the statements are built from, and which account each engine role resolves to.',
    group: 'Setup',
    permission: 'org.coa.view',
    element: <ChartOfAccounts />,
  },
  {
    path: '/setup/locations',
    title: 'Locations',
    blurb: 'Clinics and offices. Every figure in the system can be attributed to one.',
    group: 'Setup',
    permission: 'org.location.view',
    element: <Locations />,
  },
  {
    path: '/setup/departments',
    title: 'Departments',
    blurb: 'The second reporting axis, and who approves for each department.',
    group: 'Setup',
    permission: 'org.department.view',
    element: <Departments />,
  },
  {
    path: '/setup/approval-rules',
    title: 'Approval rules',
    blurb:
      'Which documents, at which amounts, go to whom — drafted by an Admin, approved by the Finance Head.',
    group: 'Setup',
    permission: 'org.approval_rule.view',
    element: <ApprovalPolicies />,
  },
  {
    path: '/setup/people',
    title: 'People & roles',
    blurb: 'Who may do what. Role changes take effect on the next request.',
    group: 'Setup',
    permission: 'org.user.view',
    element: <People />,
  },
  {
    path: '/setup/api-keys',
    title: 'API keys',
    blurb:
      'Keys for the clinic software, pasted here and used at once. Stored encrypted; never shown back.',
    group: 'Setup',
    permission: 'org.integration.configure',
    element: <ApiKeys />,
  },

  {
    path: '/audit',
    title: 'Audit trail',
    blurb: 'Append-only and hash-chained. Nothing here can be edited, by anyone.',
    group: 'Control',
    permission: 'audit.log.view',
    element: <AuditLog />,
  },
]

export const GROUP_ORDER = [
  'Overview',
  'Books',
  'Selling',
  'Buying',
  'Spending',
  'Tax',
  'Setup',
  'Control',
]

export function visibleScreens(can: (p: string) => boolean, role?: string): Screen[] {
  return SCREENS.filter(
    (s) =>
      !s.hidden && (!s.permission || can(s.permission)) && !(role && s.notForRoles?.includes(role)),
  )
}
